import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL || '';

const TOKEN_KEY = 'lexorbit_token';
const REFRESH_KEY = 'lexorbit_refresh';

const api = axios.create({ baseURL: BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function cerrarSesion() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  delete axios.defaults.headers.common['Authorization'];
  if (window.location.pathname !== '/') window.location.href = '/';
}

// Renueva el access token usando el refresh token. Usa axios "crudo" para no
// recursar en este mismo interceptor.
let _refrescando = null;
async function refrescarToken() {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) throw new Error('sin refresh token');
  const res = await axios.post(`${BASE}/api/refresh`, null, {
    headers: { Authorization: `Bearer ${refresh}` },
  });
  const nuevo = res.data.access_token;
  localStorage.setItem(TOKEN_KEY, nuevo);
  axios.defaults.headers.common['Authorization'] = `Bearer ${nuevo}`;
  return nuevo;
}

export async function asegurarToken() {
  if (!_refrescando) _refrescando = refrescarToken().finally(() => { _refrescando = null; });
  return _refrescando;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const es401 = error.response?.status === 401;

    if (es401 && original && !original._retry && localStorage.getItem(REFRESH_KEY)) {
      original._retry = true;
      try {
        const nuevo = await asegurarToken();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${nuevo}`;
        return api(original);
      } catch (e) {
        cerrarSesion();
        return Promise.reject(e);
      }
    }

    if (es401) cerrarSesion();

    // Trial vencido sin suscripción: el backend responde 402 → llevar a pagar.
    if (error.response?.status === 402 && window.location.pathname !== '/suscripcion') {
      window.location.href = '/suscripcion';
    }
    return Promise.reject(error);
  }
);

// ── Cache en memoria con TTL ──────────────────────────────────────────────────
// Para endpoints que raramente cambian (suscripcion/estado, me, etc.)
const _cache = new Map();

export function cachedGet(url, ttlMs = 120_000) {
  const hit = _cache.get(url);
  if (hit && Date.now() - hit.at < ttlMs) return Promise.resolve(hit.data);
  return api.get(url).then((r) => {
    _cache.set(url, { data: r, at: Date.now() });
    return r;
  });
}

// Invalida una URL del caché (llamar tras mutaciones que afecten esa data)
export function invalidateCache(url) {
  _cache.delete(url);
}

export default api;
